import { ID } from '@ianesthesie-kit/services';
import { PatchableMedicalService } from '../../patchableMedical.service';
import { PostopInfo } from './postopInfo.model';
import {
  ListMedicalService,
  MedicalResponse
} from '@/modules/surgicalEvent/baseMedical.service';
import {
  Emphasis,
  MedicalOption,
  postopInfoApi
} from '@/modules/surgicalEvent';
import { isNumeric } from '@ianesthesie-kit/core';

export class PostopInfoService extends ListMedicalService<PostopInfo> {
  private static INSTANCE_BY_ID: PostopInfoService;

  private constructor(patientId: string) {
    super('/postop-info', patientId);
  }

  public static getInstance(patientId: ID): PostopInfoService {
    if (this.shouldCreateNewInstance(this.INSTANCE_BY_ID, patientId)) {
      this.INSTANCE_BY_ID = new PostopInfoService(patientId);
    }

    return this.INSTANCE_BY_ID;
  }

  public async patch(
    infoPayload: Partial<PostopInfo>
  ): Promise<MedicalResponse<PostopInfo, false>> {
    if (!infoPayload.id) {
      throw new Error('Id is required for this operation');
    }

    return PatchableMedicalService.prototype.patch.call(
      this,
      await this.adjustComplicationCodes(infoPayload)
    );
  }

  private async adjustComplicationCodes(infoPayload: Partial<PostopInfo>) {
    const currentPostopInfo = await this.findInReduxOrThrow(infoPayload.id);
    let hasChanges = false;
    let newComplications: Partial<MedicalOption>[] = Array.from(
      currentPostopInfo.postopComplications ?? []
    );

    const addComplication = (code: string) => {
      if (!newComplications.find((c) => c.code === code)) {
        newComplications.push({ code, emphasis: Emphasis.IMPORTANT });
        hasChanges = true;
      }
    };

    const removeComplication = (code: string) => {
      if (newComplications.find((c) => c.code === code)) {
        newComplications = newComplications.filter((c) => c.code !== code);
        hasChanges = true;
      }
    };

    if (infoPayload.bleedingLevel === 'BLEEDING_NONE') {
      removeComplication('PostopComplication.Bleeding');
    } else if (infoPayload.bleedingLevel) {
      addComplication('PostopComplication.Bleeding');
    }

    if (infoPayload.ponvLevel === 'PONV_NONE') {
      removeComplication('PostopComplication.Nausea');
    } else if (infoPayload.ponvLevel) {
      addComplication('PostopComplication.Nausea');
    }

    if (infoPayload.temperature === null) {
      removeComplication('PostopComplication.Fever');
    } else if (isNumeric(infoPayload.temperature)) {
      if (+infoPayload.temperature < 37.5) {
        removeComplication('PostopComplication.Fever');
      } else {
        addComplication('PostopComplication.Fever');
      }
    }

    if (infoPayload.painLevel === null) {
      removeComplication('PostopComplication.Pain');
    } else if (isNumeric(infoPayload.painLevel)) {
      if (+infoPayload.painLevel < 4) {
        removeComplication('PostopComplication.Pain');
      } else {
        addComplication('PostopComplication.Pain');
      }
    }

    if (hasChanges) {
      return {
        ...infoPayload,
        postopComplications: newComplications
      };
    }

    return infoPayload;
  }

  private async findInReduxOrThrow(
    postopInfoId: ID | undefined
  ): Promise<PostopInfo> {
    if (!postopInfoId || !this.patientId) {
      throw new Error('Postop info id is not defined');
    }
    const selector = postopInfoApi.endpoints.postopInfoById.select({
      postopInfoId,
      patientId: this.patientId
    });
    const appStore = await import('@/store').then((m) => m.appStore);
    const data = selector(appStore.getState()).data;
    if (!data) {
      throw new Error('Postop info was not present in redux');
    }

    return data;
  }
}
